import {
    getServiceInvoices,
    getServiceConflicts,
    resolveServiceConflict,
} from '../../../usecases/service-usecase';
import { createCreditNote } from '../../../usecases/documents-usecase';
import { printSell, showInvoicePDF } from '../../../usecases/payments-usecase';
import { printReceipt } from '../../../utils/receipt-utils';
import { dispatchCustomEvent } from '../../../utils/dispatch';
import { createPdfBlob, createPdfUrl } from '../invoice/controller';
import { EmptyArray } from '../../../app/domain/models/empty/empty';

/**
 * Mapea los datos de la factura de servicios para ser mostrados en la tabla
 */
export function invoiceMapper(data) {
    if (!data || data.length === 0) return EmptyArray;

    return data.map((item) => ({
        ...item,
        customer: item.customer,
        state: item.state,
        date: item.date,
        consecutive: item.consecutive,
        prefix: item.prefix,
        number: item.number,
        value: item.value,
        cude: item.cufe,
        QRStr: item.QRStr
    }));
}

/**
 * Obtiene la lista de facturas de servicios
 */
export async function getInvoiceList(page = 1, limit = 8, filters = {}) {
    try {
        const { status, date_from, date_to } = filters;
        const data = await getServiceInvoices(page, limit, status, date_from, date_to);
        if (!data.data || data.data.length === 0) {
            return { ...data, data: EmptyArray };
        }
        return { ...data, data: invoiceMapper(data.data) };
    } catch (error) {
        console.error('Error fetching services invoices:', error);
        throw error;
    }
}

/**
 * Mapea los servicios en conflicto para la tabla
 */
export function conflictedSalesMapper(data) {
    if (!data || data.length === 0) return EmptyArray;

    return data.map((item) => ({
        date: item.date,
        customer: item.customer,
        document: item.prefix && item.number ? `${item.prefix}-${item.number}` : 'N/A',
        total: item.total,
        error_message: item.error_message || '',
        bill_id: item.bill_id,
        conflict_type: item.conflict_type || 'API_EXCEPTION',
        thirds_id: item.thirds_id,
        customer_id: item.customer_id,
        _original: item,
    }));
}

/**
 * Obtiene la lista de servicios en conflicto
 */
export async function getConflictedSalesList(page = 1, limit = 8) {
    try {
        const data = await getServiceConflicts(page, limit);
        if (!data.data || data.data.length === 0) {
            return { ...data, data: EmptyArray };
        }
        return { ...data, data: conflictedSalesMapper(data.data) };
    } catch (error) {
        console.error('Error fetching services conflicted sales:', error);
        throw error;
    }
}

/**
 * Resuelve un conflicto de servicio
 */
export async function handleResolveConflict(bill_id, force_generate = false) {
    try {
        dispatchCustomEvent('loading-btn');
        const response = await resolveServiceConflict(bill_id, force_generate);
        dispatchCustomEvent('loading-btn');

        if (response?.requires_confirmation || response?.data?.requires_confirmation) {
            return { requires_confirmation: true, message: response.message };
        }

        dispatchCustomEvent('popup', { message: response.message || "Conflicto resuelto", type: "success" });
        return { success: true };
    } catch (error) {
        dispatchCustomEvent('loading-btn');
        const message = error.response?.data?.message || error.message || "Error al resolver conflicto";
        dispatchCustomEvent('popup', { message, type: "error" });
        return { success: false, message };
    }
}

/**
 * Anula una factura de servicio creando una nota crédito
 */
export async function submitCreditNote(data) {
    try {
        await createCreditNote(data.prefix, data.number);
        dispatchCustomEvent('popup', { message: "Nota crédito creada con éxito", type: "success" });
    } catch (error) {
        console.error('Error creating credit note for service:', error);
        dispatchCustomEvent('popup', { message: error?.response?.data?.message || "Error al crear nota crédito", type: "error" });
        throw error;
    }
}

export async function onReprint(row, profile) {
    const item = row._original || row;
    try {
        const sellData = await printSell(item.bill_id);
        printReceipt(sellData, profile);
    } catch (error) {
        console.error('Error reprinting service receipt:', error);
        dispatchCustomEvent('popup', { message: "Error al reimprimir la tirilla", type: "error" });
    }
}

export async function onDownloadPdf(row) {
    const item = row._original || row;
    try {
        const base64Pdf = await showInvoicePDF(item.cufe || item.cude);
        const blob = createPdfBlob(base64Pdf);
        const url = createPdfUrl(blob);
        window.open(url, '_blank');
    } catch (error) {
        console.error('Error downloading service invoice PDF:', error);
        dispatchCustomEvent('popup', { message: "Error al descargar el PDF de la factura", type: "error" });
    }
}

